import React, { useId } from 'react';
import { cn } from './utils/cn';
import './sekaDealCard.css';

const suitSymbols = {
  hearts: '♥',
  diamonds: '♦',
  clubs: '♣',
  spades: '♠',
};

const suitColors = {
  hearts: 'text-red-500',
  diamonds: 'text-red-500',
  clubs: 'text-slate-900',
  spades: 'text-slate-900',
};

const suitGradients = {
  hearts: 'from-red-500/10 to-transparent',
  diamonds: 'from-red-500/10 to-transparent',
  clubs: 'from-slate-500/10 to-transparent',
  spades: 'from-slate-500/10 to-transparent',
};

const sizesMap = {
  /** Половина от `sm` (например мини-превью последней взятки). */
  xxs: {
    card: 'w-6 h-8',
    rank: 'text-[6px] leading-none',
    suit: 'text-[7px] leading-none',
    center: 'text-sm',
    cornerTop: 'top-px left-px',
    cornerBottom: 'bottom-px right-px',
    centerOpacity: 'opacity-100',
  },
  xs: {
    card: 'w-10 h-14',
    rank: 'text-[10px]',
    suit: 'text-xs',
    center: 'text-xl',
    cornerTop: 'top-0.5 left-0.5',
    cornerBottom: 'bottom-0.5 right-0.5',
    centerOpacity: 'opacity-100',
  },
  sm: {
    card: 'w-12 h-16',
    rank: 'text-xs',
    suit: 'text-sm',
    center: 'text-2xl',
    cornerTop: 'top-0.5 left-0.5',
    cornerBottom: 'bottom-0.5 right-0.5',
    centerOpacity: 'opacity-100',
  },
  md: {
    card: 'w-16 h-[5.5rem]',
    rank: 'text-base',
    suit: 'text-xl',
    center: 'text-3xl',
    cornerTop: 'top-1 left-1',
    cornerBottom: 'bottom-1 right-1',
    centerOpacity: 'opacity-100',
  },
  lg: {
    card: 'w-20 h-28',
    rank: 'text-lg',
    suit: 'text-2xl',
    center: 'text-4xl',
    cornerTop: 'top-1.5 left-1.5',
    cornerBottom: 'bottom-1.5 right-1.5',
    centerOpacity: 'opacity-100',
  },
};

/** Углы ранга/масти ближе к краю (козырь у колоды, частичное перекрытие). Размеры шрифтов те же. */
const tightCornersBySize = {
  xxs: { cornerTop: 'top-0 left-0', cornerBottom: 'bottom-0 right-0' },
  xs: { cornerTop: 'top-0 left-0', cornerBottom: 'bottom-0 right-0' },
  sm: { cornerTop: 'top-0 left-0', cornerBottom: 'bottom-0 right-0' },
  md: { cornerTop: 'top-0.5 left-0.5', cornerBottom: 'bottom-0.5 right-0.5' },
  lg: { cornerTop: 'top-1 left-1', cornerBottom: 'bottom-1 right-1' },
};

export function PlayingCard({
  suit,
  rank,
  faceDown = false,
  className,
  size = 'md',
  highlighted = false,
  /** Узкий инсет угловых индексов (ранг/масть), без смены размеров текста. */
  tightCorners = false,
}) {
  const sizes = sizesMap[size];
  const tight = tightCornersBySize[size] || tightCornersBySize.sm;
  const cornerTop = tightCorners ? tight.cornerTop : sizes.cornerTop;
  const cornerBottom = tightCorners ? tight.cornerBottom : sizes.cornerBottom;
  const patternId = `card-hex-${useId().replace(/:/g, '')}`;

  if (faceDown) {
    return (
      <div
        className={cn(
          sizes.card,
          size === 'xxs' ? 'rounded-md border border-neon-cyan/45 shadow-md shadow-black/50' : 'rounded-xl border-2 border-neon-cyan/40 shadow-xl shadow-black/60',
          'overflow-hidden relative transition-none',
          highlighted && 'shadow-[0_0_20px_rgba(0,255,255,0.5)] border-neon-cyan',
          className
        )}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-black" aria-hidden />
        <div
          className="absolute inset-0 opacity-[0.35] bg-[repeating-linear-gradient(60deg,transparent,transparent_3px,rgba(34,211,238,0.12)_3px,rgba(34,211,238,0.12)_6px)]"
          aria-hidden
        />
        <div
          className="absolute inset-0 opacity-[0.25] bg-[repeating-linear-gradient(-60deg,transparent,transparent_5px,rgba(6,182,212,0.1)_5px,rgba(6,182,212,0.1)_10px)]"
          aria-hidden
        />
        <svg className="absolute inset-0 h-full w-full text-cyan-400/30" viewBox="0 0 100 140" aria-hidden>
          <defs>
            <pattern
              id={patternId}
              width="14"
              height="24"
              patternUnits="userSpaceOnUse"
              patternTransform="scale(0.9)"
            >
              <path
                d="M7 2 L12 7 L12 17 L7 22 L2 17 L2 7 Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="0.6"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill={`url(#${patternId})`} />
        </svg>
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/5 via-transparent to-cyan-500/10" />
      </div>
    );
  }

  return (
    <div
      className={cn(
        sizes.card,
        size === 'xxs' ? 'rounded-md' : 'rounded-xl',
        'bg-gradient-to-br from-white via-slate-50 to-slate-100 border border-slate-200 shadow-xl shadow-black/60 relative overflow-hidden transition-none',
        highlighted && 'shadow-[0_0_25px_rgba(255,215,0,0.6)] border-neon-gold scale-105',
        className
      )}
    >
      <div className={cn('absolute inset-0 bg-gradient-to-br pointer-events-none', suitGradients[suit])} />

      <div className={cn('absolute z-10 leading-[0.95]', cornerTop, suitColors[suit])}>
        <span className={cn('block font-bold', sizes.rank)}>{rank}</span>
        <span className={cn('-mt-0.5 block', sizes.suit)}>{suitSymbols[suit]}</span>
      </div>

      <div className={cn('absolute inset-0 flex items-center justify-center', suitColors[suit])}>
        <span className={cn(sizes.center, sizes.centerOpacity)}>{suitSymbols[suit]}</span>
      </div>

      <div className={cn('absolute z-10 rotate-180 leading-[0.95]', cornerBottom, suitColors[suit])}>
        <span className={cn('block font-bold', sizes.rank)}>{rank}</span>
        <span className={cn('-mt-0.5 block', sizes.suit)}>{suitSymbols[suit]}</span>
      </div>

      <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent pointer-events-none" />
    </div>
  );
}

export function CardPair({
  cards,
  faceDown = false,
  /** Если задан — по слоту; иначе единый `faceDown` (анимация раздачи и т.п.) */
  faceDownByIndex,
  className,
  size = 'md',
  /** Ключи для remount при смене фазы раздачи (запуск CSS-анимации). */
  cardKeys,
  /** Ref на обёртку каждого слота (измерение цели анимации раздачи). */
  slotRefs,
  /**
   * По слоту: показать карту. Если `false` — невидимый плейсхолдер того же размера (раздача Local Seka).
   * Не передавайте — все слоты с картами как раньше.
   */
  showCardByIndex,
  /** Клик по слоту (например локальный белот / армянский режим). */
  onSlotClick,
  /** Доп. классы на `PlayingCard` по индексу слота. */
  getSlotClassName,
}) {
  const sizes = sizesMap[size];
  const n = cards.length;
  const isCompactXs = size === 'xs';
  /** Узкая стопка (белот 6–8 карт у соперника): сильнее перекрываем, иначе ряд раздувает стол. */
  const tightXsStrip = isCompactXs && n >= 6;
  const spacing = isCompactXs
    ? tightXsStrip
      ? '-space-x-8'
      : n <= 2
        ? '-space-x-5'
        : n === 3
          ? '-space-x-4'
          : '-space-x-3'
    : n <= 2
      ? '-space-x-4'
      : n === 3
        ? '-space-x-3'
        : '-space-x-2';
  const rot = (i) => {
    if (n <= 1) return '';
    if (isCompactXs) {
      if (tightXsStrip) return i === 0 ? '-rotate-1' : i === n - 1 ? 'rotate-1' : '';
      if (n === 2) return i === 0 ? '-rotate-4' : 'rotate-4';
      if (n === 3) return i === 0 ? '-rotate-4' : i === 1 ? 'rotate-1' : 'rotate-4';
      return i === 0 ? '-rotate-2' : i === n - 1 ? 'rotate-2' : 'rotate-1';
    }
    if (n === 2) return i === 0 ? '-rotate-6' : 'rotate-6';
    if (n === 3) return i === 0 ? '-rotate-6' : i === 1 ? 'rotate-1' : 'rotate-6';
    return i === 0 ? '-rotate-3' : i === n - 1 ? 'rotate-3' : 'rotate-1';
  };
  const fd = (i) =>
    Array.isArray(faceDownByIndex) && faceDownByIndex.length > i ? Boolean(faceDownByIndex[i]) : faceDown;
  const slotShowsCard = (i) =>
    !Array.isArray(showCardByIndex) || showCardByIndex.length <= i ? true : Boolean(showCardByIndex[i]);
  const stripNoHoverGrow = tightXsStrip;
  return (
    <div
      className={cn(
        'flex w-max min-w-0 max-w-full justify-center',
        spacing,
        className
      )}
    >
      {cards.map((card, i) => {
        const slotExtra =
          typeof getSlotClassName === 'function' ? getSlotClassName(i) : getSlotClassName || '';
        const faceDown = fd(i);
        const cardEl = slotShowsCard(i) ? (
          <PlayingCard
            suit={card.suit}
            rank={card.rank}
            faceDown={faceDown}
            size={size}
            className={cn(
              !stripNoHoverGrow &&
                !faceDown &&
                'transition-transform duration-150 hover:z-10 hover:scale-110',
              cardKeys && 'seka-deal-card-pop',
              slotExtra
            )}
          />
        ) : (
          <div className={cn(sizes.card, 'invisible shrink-0')} aria-hidden />
        );
        return (
          <div
            key={cardKeys?.[i] ?? i}
            ref={slotRefs?.[i] ?? undefined}
            className={cn(rot(i), 'inline-block origin-bottom')}
          >
            {slotShowsCard(i) && typeof onSlotClick === 'function' ? (
              <button
                type="button"
                className="m-0 inline-block border-0 bg-transparent p-0"
                onClick={() => onSlotClick(i)}
              >
                {cardEl}
              </button>
            ) : (
              cardEl
            )}
          </div>
        );
      })}
    </div>
  );
}
